import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const lastUpdated = '23 August 2026';

export const LegalPageHero = ({ title, description, currentPage, meta }) => (
  <section className="page-hero-shell tech-grid border-b border-white/10 bg-[#071327] py-10 md:py-14">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
        className="grid gap-8 lg:grid-cols-[1fr_22rem] lg:items-end"
      >
        <div>
          <div className="mb-5 flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <span className="text-white">{currentPage}</span>
          </div>
          <span className="section-eyebrow">Legal</span>
          <h1 className="font-heading mt-4 max-w-4xl text-3xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-4xl md:text-6xl">
            {title}
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-8 text-slate-300 md:text-lg">
            {description}
          </p>
        </div>

        <dl className="grid grid-cols-2 border border-white/10 bg-white/[0.045]">
          {(meta || [
            ['Updated', lastUpdated],
            ['Status', 'Draft'],
          ]).map(([label, value]) => (
            <div key={label} className="border-r border-white/10 p-4 last:border-r-0 lg:border-r-0 lg:border-t lg:first:border-t-0">
              <dt className="font-['JetBrains_Mono'] text-[0.68rem] font-bold uppercase tracking-[0.24em] text-[#93C5FD]">
                {label}
              </dt>
              <dd className="mt-2 text-sm font-semibold text-white">{value}</dd>
            </div>
          ))}
        </dl>
      </motion.div>
    </div>
  </section>
);

export const LegalPageBody = ({ children, aside }) => (
  <main className="bg-[#0B1F3B] py-10 text-white md:py-14">
    <div className="mx-auto flex max-w-6xl flex-col gap-7 px-4 sm:px-6 lg:px-8">
      {aside ? aside : null}
      <article className="min-w-0" aria-label="Legal content">
        {children}
      </article>
    </div>
  </main>
);

export const LegalNav = ({ title = 'On this page', sections }) => (
  <nav aria-label={title} className="border border-[#60A5FA]/25 bg-[#102548] p-4 shadow-[0_18px_45px_rgba(2,6,23,0.22)]">
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
      <h2 className="shrink-0 font-['JetBrains_Mono'] text-[0.7rem] font-bold uppercase tracking-[0.22em] text-[#0369A1]">
        Page sections
      </h2>
      <ul className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0">
      {sections.map((section, index) => (
        <li key={section.id || section.label}>
          <a
            href={section.id ? `#${section.id}` : section.href}
              className="inline-flex min-h-11 items-center gap-2 whitespace-nowrap border border-[#60A5FA]/20 bg-[#0B1F3B] px-3 text-sm font-semibold text-slate-200 transition-colors hover:border-[#93C5FD] hover:bg-[#12345f] hover:text-white"
          >
              <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0369A1]">
              {String(index + 1).padStart(2, '0')}
            </span>
            <span>{section.label}</span>
          </a>
        </li>
      ))}
      </ul>
    </div>
  </nav>
);

export const LegalSection = ({ id, title, children }) => (
  <section id={id} className="legal-section-card scroll-mt-32 border border-[#60A5FA]/20 bg-[#102548] p-5 shadow-[0_18px_42px_rgba(2,6,23,0.22)] md:p-8">
    <div className="grid gap-5 lg:grid-cols-[13rem_minmax(0,1fr)]">
      <h2 className="font-heading text-xl font-extrabold uppercase leading-snug tracking-tight text-white md:text-2xl">
        {title}
      </h2>
      <div className="legal-copy flex max-w-[72ch] flex-col gap-4 text-base leading-8 text-slate-300">
        {children}
      </div>
    </div>
  </section>
);

export const LegalPanel = ({ children }) => (
  <div className="legal-section-stack flex flex-col gap-4">
    {children}
  </div>
);

export const ReviewNotice = () => (
  <div className="border border-[#60A5FA]/35 bg-[#0f2f55] p-5 shadow-[0_18px_42px_rgba(2,6,23,0.22)]">
    <p className="font-['JetBrains_Mono'] text-xs font-bold uppercase tracking-[0.22em] text-[#0369A1]">
      Client review draft
    </p>
    <p className="mt-3 max-w-[72ch] text-sm leading-7 text-slate-200">
      This page is prepared for client review and is not final legal advice. Confirm each placeholder before publishing as final.
    </p>
  </div>
);

export { lastUpdated };
